// StudentMathResponse — Componente de respuesta para preguntas matemáticas.
// El estudiante puede:
//   - Escribir su respuesta en LaTeX (con barra extendida de símbolos)
//   - Ver la fórmula renderizada en tiempo real
//   - Adjuntar UNA foto del proceso resuelto a mano (se sube a Cloudinary)
//
// El valor que se devuelve a `onChange` es un objeto:
//   { latex: "x=5", photo_url: "https://res.cloudinary.com/..." }
// Mantiene retro-compatibilidad: si onChange recibe un string del padre se
// trata como `{latex: string}`.
import { useRef, useState } from "react";
import { Camera, X, Loader2 } from "lucide-react";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MATH_PALETTES } from "@/components/MathFormulaEditor";

export default function StudentMathResponse({
  value, onChange, assignmentId, questionId, placeholder = "Escribe tu respuesta…",
}) {
  // Permite que `value` venga como string (legacy) o como objeto {latex, photo_url}.
  const cur = typeof value === "object" && value !== null ? value : { latex: value || "", photo_url: null };
  const [palette, setPalette] = useState("basicas");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const setLatex = (l) => onChange({ ...cur, latex: l });
  const setPhoto = (url) => onChange({ ...cur, photo_url: url });

  const insertSym = (sym) => setLatex(`${cur.latex || ""}${cur.latex ? " " : ""}${sym}`);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!assignmentId || !questionId) {
      toast.error("Falta contexto para subir la foto.");
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      toast.error("La foto pesa demasiado (máx. 6 MB).");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/questionnaire-assignments/${assignmentId}/upload-process-photo`,
        fd,
        { params: { question_id: questionId }, headers: { "Content-Type": "multipart/form-data" } },
      );
      setPhoto(data.url);
      toast.success("Foto del proceso subida ✓");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error subiendo la foto");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3 max-w-2xl" data-testid={`student-math-${questionId}`}>
      {/* Input + live preview */}
      <div className="grid sm:grid-cols-2 gap-2">
        <textarea
          rows={3}
          value={cur.latex || ""}
          onChange={(e) => setLatex(e.target.value)}
          placeholder={placeholder}
          data-testid={`student-math-input-${questionId}`}
          className="w-full px-3 py-2 rounded-xl border-2 border-[#E1E5F0] outline-none focus:border-[#A43790] font-mono text-sm"
        />
        <div className="rounded-xl border-2 border-dashed border-[#E1E5F0] bg-white p-3 min-h-[80px] flex items-center justify-center" data-testid={`student-math-preview-${questionId}`}>
          {cur.latex ? (
            <SafeMath latex={cur.latex} />
          ) : (
            <p className="text-[11px] text-[#6E7591] italic">Vista previa de tu fórmula.</p>
          )}
        </div>
      </div>

      {/* Barra de símbolos categorizada */}
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-1" data-testid={`student-math-palettes-${questionId}`}>
          {Object.entries(MATH_PALETTES).map(([k, p]) => (
            <button key={k} type="button" onClick={() => setPalette(k)}
              data-testid={`student-math-palette-${questionId}-${k}`}
              className={`text-[10px] font-bold px-2.5 py-1 rounded-full border-2 ${palette === k ? "bg-[#1F2547] border-[#1F2547] text-white" : "bg-white border-[#E1E5F0] text-[#4C5478] hover:border-[#A43790]"}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-6 sm:grid-cols-10 gap-1 bg-[#F7F8FA] rounded-lg p-1.5 border border-[#E1E5F0]">
          {MATH_PALETTES[palette].items.map((it, i) => (
            <button key={i} type="button" onClick={() => insertSym(it.latex)} title={it.latex}
              data-testid={`student-math-sym-${questionId}-${palette}-${i}`}
              className="min-h-[32px] px-1.5 py-1 rounded border-2 border-[#E1E5F0] bg-white hover:border-[#A43790] hover:bg-[#FFF1F8] text-xs font-bold text-[#1F2547]">
              <span style={{ fontSize: it.display.length > 4 ? "9px" : "12px" }}>{it.display}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Foto del proceso a mano */}
      <div className="rounded-xl border-2 border-[#E1E5F0] bg-[#FAFBFE] p-3 space-y-2" data-testid={`student-math-photo-${questionId}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[11px] uppercase tracking-wider font-extrabold text-[#A43790] flex items-center gap-1">
            <Camera size={12} /> Foto del proceso (opcional)
          </p>
          {!cur.photo_url && (
            <button type="button" onClick={() => fileRef.current?.click()}
              disabled={uploading || !assignmentId}
              data-testid={`student-math-photo-pick-${questionId}`}
              className="text-[11px] font-bold inline-flex items-center gap-1 bg-[#2C49A0] text-white rounded-full px-3 py-1 hover:bg-[#1F2547] disabled:opacity-40">
              {uploading ? <><Loader2 size={11} className="animate-spin" /> Subiendo…</> : <><Camera size={11} /> Subir foto</>}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid={`student-math-photo-input-${questionId}`} />
        </div>
        {cur.photo_url && (
          <div className="flex items-start gap-2">
            <img src={cur.photo_url} alt="Mi proceso a mano" className="max-h-40 rounded border-2 border-[#E1E5F0]" />
            <button type="button" onClick={() => setPhoto(null)}
              data-testid={`student-math-photo-remove-${questionId}`}
              className="text-[#A52258] hover:text-[#D43790]" title="Quitar foto">
              <X size={14} />
            </button>
          </div>
        )}
        {!cur.photo_url && (
          <p className="text-[10px] text-[#6E7591] italic">
            Si lo prefieres, resuelve en papel y sube una foto. Tu docente la verá junto a tu respuesta.
          </p>
        )}
      </div>
    </div>
  );
}

function SafeMath({ latex }) {
  try {
    return <BlockMath math={latex} />;
  } catch (e) {
    return <span className="text-[11px] text-[#A52258] italic">Sintaxis inválida</span>;
  }
}
